// ---------------------------------------------------------------------------
// Photo uploads, shared by creating and editing a post:
//   1. ask Django for presigned URLs for a batch of files
//   2. PUT each file directly to Spaces
//   3. attach each uploaded Spaces key to the post as a photo record
// ---------------------------------------------------------------------------

import { api } from "./client";

// Response shape:
// { "urls": [{ "filename": "photo1.jpg", "upload_url": "https://...", "content_type": "image/jpeg" }] }
export async function fetchPresignedUrls(token, files) {
  const data = await api.post("/family/api/presigned-upload/", token, {
    filenames: files.map((f) => f.name),
  });

  if (!Array.isArray(data?.urls)) {
    throw new Error("Unexpected response when preparing image uploads.");
  }

  if (data.urls.length !== files.length) {
    throw new Error(
      `Expected ${files.length} upload URL(s) but received ${data.urls.length}.`,
    );
  }

  return data.urls.map((entry, i) => ({
    file: files[i],
    presignedUrl: entry.upload_url,
    contentType: entry.content_type,
    // Extract the Spaces object key from the presigned URL path.
    // e.g. https://bucket.syd1.digitaloceanspaces.com/portfolio/media/family/abc/photo.jpg?X-Amz-...
    //   -> portfolio/media/family/abc/photo.jpg
    spacesKey: new URL(entry.upload_url).pathname.replace(/^\//, ""),
  }));
}

// Uses content_type returned by Django rather than inferring from the File.
// Not an API call: the presigned URL carries its own auth.
export async function uploadFileToSpaces({ file, presignedUrl, contentType }) {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType ?? file.type },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed for "${file.name}" (${response.status}).`);
  }
}

export function attachPhotoToPost(token, postGuid, spacesKey) {
  return api.post(`/family/posts/${postGuid}/photos/`, token, {
    spaces_key: spacesKey,
  });
}

export function deletePhoto(token, postGuid, photoGuid) {
  return api.delete(`/family/posts/${postGuid}/photos/${photoGuid}/`, token);
}
